interface IDateHelper {
  formatDate: (date: Date) => string
  formatTime: (date: Date) => string
  formatDateShort: (date: Date) => string
  formatToDate: (date: Date) => string
  formatDateDayAndMonth: (date: Date) => string
}


export default (): IDateHelper => {
  /**
   * Helper function to capitalize the first character of a string
   * @param value - The string to capitalize
   * @returns The string with the first character capitalized
   * @example
   * firstCharToUpper('hello') // 'Hello'
   */
  function firstCharToUpper(value: string): string {
    if (value !== '') {
      return value.charAt(0).toUpperCase() + value.slice(1)
    }
    return value
  }

  /**
   * Formats a date to a specific format
   * @param date - The date to format
   * @returns The formatted date string
   * @example
   * formatDate(new Date()) // 'Söndag 25 december'
   */
  function formatDate(date: Date): string {
    return firstCharToUpper(
      date.toLocaleString(import.meta.env.VITE_Culture, {
        weekday: 'long',
        day: 'numeric',
        month: 'long',
      })
    )
  }

  /**
   * Formats a date to a specific format
   * @param date - The date to format
   * @returns The formatted date string
   * @example
   * formatDateDayAndMonth(new Date()) // '25 dec'
   */
  function formatDateDayAndMonth(date: Date): string {
    return firstCharToUpper(
      date
        .toLocaleString(import.meta.env.VITE_Culture, {
          day: 'numeric',
          month: 'short',
        })
        .replace(/\.$/, '')
    )
  }

  /**
   * Formats a date to a specific format
   * @param date - The date to format
   * @returns The formatted date string
   * @example
   * formatTime(new Date()) // '15:30'
   */
  function formatTime(date: Date): string {
    return firstCharToUpper(
      date.toLocaleString(import.meta.env.VITE_Culture, {
        hour: '2-digit',
        minute: '2-digit',
      })
    )
  }

  /**
   * Formats a date to a specific format
   * @param date - The date to format
   * @returns The formatted date string
   * @example
   * formatDateShort(new Date()) // '25 december'
   */
  function formatDateShort(date: Date): string {
    return date.toLocaleString(import.meta.env.VITE_Culture, {
      day: 'numeric',
      month: 'long',
    })
  }

  function formatToDate(date: Date): string {
    return date.toISOString().split('T')[0]
  }

  return {
    formatDate,
    formatTime,
    formatDateShort,
    formatToDate,
    formatDateDayAndMonth,
  }
}
